import { ActionSplitButton } from "@/components/ui/ActionSplitButton/ActionSplitButton";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import type { ContactPhone } from "@/lib/types/Contact";
import { formatLocalOrIntlPhoneNumber } from "@/lib/utils/format";
import type { ButtonProps } from "@/components/ui/shadcn/Button/Button";
import { Phone, PhoneCall } from "lucide-react";

type CallablePhone = Pick<
  ContactPhone,
  "extension" | "isPrimary" | "label" | "number"
>;

interface ContactCallSplitButtonProps {
  className?: string;
  contactId?: string;
  phones: readonly CallablePhone[];
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
}

const callPhone = (phone: CallablePhone | undefined) => {
  if (!phone || typeof window === "undefined") {
    return;
  }

  const sanitizedNumber = phone.number.replace(/[^0-9+]/g, "");
  if (!sanitizedNumber) {
    return;
  }

  window.open(`tel:${sanitizedNumber}`, "_self");
};

const getPhoneLabel = (phone: CallablePhone) => {
  const formattedNumber = formatLocalOrIntlPhoneNumber(phone);
  return phone.label ? `${phone.label}: ${formattedNumber}` : formattedNumber;
};

export function ContactCallSplitButton({
  className,
  contactId,
  phones,
  size,
  variant = "outline",
}: ContactCallSplitButtonProps) {
  const { openPhoneCallLogModal } = useGlobalModal();
  const callablePhones = phones.filter((phone) => phone.number.trim());
  const primaryPhone =
    callablePhones.find((phone) => phone.isPrimary) ?? callablePhones.at(0);
  const alternatePhones = callablePhones.filter(
    (phone) => phone !== primaryPhone,
  );
  const callLogActions = contactId
    ? [
        {
          icon: PhoneCall,
          label: "Call log",
          onSelect: () => {
            openPhoneCallLogModal({ contactId });
          },
        },
      ]
    : [];

  return (
    <ActionSplitButton
      actions={[
        ...alternatePhones.map((phone) => ({
          icon: Phone,
          label: getPhoneLabel(phone),
          onSelect: () => {
            callPhone(phone);
          },
        })),
        ...callLogActions,
      ]}
      className={className}
      icon={Phone}
      label="Call"
      onClick={() => {
        callPhone(primaryPhone);
      }}
      primaryDisabled={!primaryPhone}
      size={size}
      variant={variant}
    />
  );
}
